import { ASSIGNABLE_USER_TYPES, REQUIRE_ROLE_FOR_MEMBER } from '../constants/accessRules.js';

const lower = (value) => String(value ?? '').trim().toLowerCase();
const ADMIN_WORDS = ['admin', 'administrator', 'administrador'];
const OWNER_WORDS = ['owner', 'propietario', 'propietaria', 'dueño', 'dueña'];

/**
 * A row's access, or `null` for "No access yet". With `REQUIRE_ROLE_FOR_MEMBER` a Member with
 * no role has no access (groups alone grant nothing in V1); an Admin may have no roles.
 *
 * @param {{ user_type: string, role_ids: string[], group_ids: string[] } | null} access
 */
export function normalizeAccess(access) {
  if (!access || !ASSIGNABLE_USER_TYPES.includes(access.user_type)) return null;
  if (REQUIRE_ROLE_FOR_MEMBER && access.user_type === 'member' && access.role_ids.length === 0) {
    return null;
  }
  return { user_type: access.user_type, role_ids: [...access.role_ids], group_ids: [...access.group_ids] };
}

const idsByName = (names, catalogItems) =>
  names
    .map((name) => catalogItems.find((item) => lower(item.name) === lower(name))?.id)
    .filter(Boolean);

/** The file asks for the Owner (in either language): that is never importable (CT-5636 / CT-5834). */
export const isOwnerWord = (raw) => OWNER_WORDS.includes(lower(raw));

/** `'admin'` or `'member'` from the user type written in the file (Admin in either language). */
export const fileUserType = (raw) => (ADMIN_WORDS.includes(lower(raw)) ? 'admin' : 'member');

/**
 * The access a row's file asks for ("Apply access from file"): user type (Admin or Member),
 * roles and groups matched by name; names that do not exist are ignored.
 *
 * @param {{ accessFromFile: { user_type: string, roles: string[], groups: string[] } }} row
 * @param {{ roles: Array<{ id: string, name: string }>, groups: Array<{ id: string, name: string }> }} catalog
 */
export function accessFromFile(row, catalog) {
  const { user_type, roles, groups } = row.accessFromFile;
  return normalizeAccess({
    user_type: fileUserType(user_type),
    role_ids: idsByName(roles, catalog.roles),
    group_ids: idsByName(groups, catalog.groups),
  });
}
